/**
 * Inbox-row badge for the Merlin edit proposal an email produced — the
 * change-request counterpart of the order badge (owner direction, review
 * 0vlvv8: show the proposal on the email itself, the way an order is shown).
 *
 * Pure so the label/variant/tooltip rules are unit-tested without rendering.
 */
import type { PortalEmailEditProposal } from '../types/portal';
import type { TagVariant } from '../components/portal/EmailTag';
import { formatDate } from './dateFormatters';

export interface EditProposalTag {
  variant: TagVariant;
  label: string;
  title: string;
}

function businessDays(n: number): string {
  return `${n} business day${n === 1 ? '' : 's'}`;
}

export function editProposalTag(
  proposal: PortalEmailEditProposal | null | undefined,
): EditProposalTag | null {
  if (!proposal) return null;
  const orderRef = proposal.sage_order_no ? ` on ${proposal.sage_order_no}` : '';
  switch (proposal.status) {
    case 'proposed':
      if (proposal.is_aging) {
        return {
          variant: 'change-proposed',
          label: 'Change waiting',
          title: `Customer change${orderRef} un-answered for ${businessDays(
            proposal.business_days_open,
          )} — it ships without the change unless someone confirms or dismisses it. ${proposal.summary}`,
        };
      }
      return {
        variant: 'change-proposed',
        label: 'Change proposed',
        title: `Merlin proposed a change${orderRef} from this email — open it to confirm or dismiss. ${proposal.summary}`,
      };
    case 'confirmed': {
      // A revision confirmed onto a portal DRAFT (kanban 3db03d35) has no
      // Sage write at all: the draft carries it to Sage when submitted.
      if (proposal.applied_to_draft) {
        return {
          variant: 'change-applied',
          label: 'Applied to draft',
          title: `The change was confirmed and applied to the draft order; it goes to Sage when the draft is submitted. ${proposal.summary}`,
        };
      }
      // "Applied" only once Sage confirmed the write — a failed push must
      // not read as done (owner ask: our system sees the change IN Sage).
      const ws = proposal.sage_write_status ?? null;
      if (ws === 'failed') {
        return {
          variant: 'ai-failed',
          label: 'Sage push failed',
          title: `The CSR confirmed the change${orderRef} but the push to Sage failed — it is flagged for follow-up and not applied in Sage. ${proposal.summary}`,
        };
      }
      if (ws === 'confirmed') {
        return {
          variant: 'change-applied',
          label: 'Applied in Sage',
          title: `The change${orderRef} was confirmed and Sage shows it applied. ${proposal.summary}`,
        };
      }
      return {
        variant: 'change-applied',
        label: 'Sent to Sage',
        title: `The change${orderRef} was confirmed and sent to Sage; waiting for Sage to confirm it landed. ${proposal.summary}`,
      };
    }
    case 'dismissed':
      return {
        variant: 'change-settled',
        label: 'Change dismissed',
        title: `Dismissed by a CSR${proposal.dismiss_reason ? `: ${proposal.dismiss_reason}` : ''}; nothing was applied. ${proposal.summary}`,
      };
    case 'expired':
      if (proposal.expired_reason === 'already_applied' && proposal.sales_site_ship_date) {
        // Settled by the sales-site schedule, not by Sage (AU02011): never
        // say "applied in Sage" for a change Sage does not show.
        return {
          variant: 'change-applied',
          label: 'Scheduled in sales site',
          title: `The sales site already scheduled ship date ${formatDate(
            proposal.sales_site_ship_date,
          )}${orderRef} and emailed the customer the confirmation, so the proposal was closed. Sage's required-by date was not changed. ${proposal.summary}`,
        };
      }
      if (proposal.expired_reason === 'already_applied') {
        return {
          variant: 'change-applied',
          label: 'Applied in Sage',
          title: `Sage already shows this change${orderRef} (keyed by hand), so the proposal was closed. ${proposal.summary}`,
        };
      }
      if (proposal.expired_reason === 'answered_by_desk') {
        // A notes-only card (no Sage field) the desk answered by email from
        // the portal: the reply was the answer, nothing was left to apply.
        return {
          variant: 'change-settled',
          label: 'Answered by email',
          title: `The desk answered the customer on this thread${orderRef}; the note had no Sage field to apply, so the card was closed. ${proposal.summary}`,
        };
      }
      return {
        variant: 'change-settled',
        label: 'Change expired',
        title: `The order${orderRef} closed in Sage before anyone acted — this change was never applied. ${proposal.summary}`,
      };
    default:
      return null;
  }
}
